import { Link } from "react-router";
import { useApi } from "../../api";
import type { PortalConfig } from "../../config";
import { Bars, HBars, prettyDay, RangePicker, Split, Tile, usd, useRange, type Range } from "./common";

interface Tiles {
  sales: number; refunds: number; transactions: number; paidTickets: number; revenue: number; avgInvoice: number; expenses: number; profit: number;
  insurance: number; tax: number; newLeads: number; checkedIn: number; shipped: number;
}
interface Analytics {
  range: Range; previous: Range; tiles: Tiles; before: Tiles;
  monthly: { month: string; sales: number; lastYear: number }[];
  weekly: { week: string; checkedIn: number; shipped: number }[];
  conversion: { leads: number; arrived: number; archived: number; open: number };
  approval: { sent: number; accepted: number; declined: number; waiting: number };
  turnaround: { tickets: number; avgDays: number | null; medianDays: number | null };
  services: { name: string; revenue: number; count: number }[];
  methods: { method: string; amount: number; count: number }[];
  pipeline: { status: string; count: number }[];
  states: { state: string; amount: number }[];
}

const METHOD: Record<string, string> = { card: "Card", check: "Check", check_by_mail: "Check by mail", paypal: "PayPal", venmo: "Venmo", bank: "Bank", other: "Other" };
const monthLabel = (m: string) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", year: "2-digit" });
const weekLabel = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
const compact = (n: number) => (n >= 1000 ? `$${Math.round(n / 100) / 10}k` : usd(n, false));
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : "—");

/**
 * Dashboard (owner, 2026-11-04): the business at a glance for a date range, compared with the same length of time
 * before. Sales are by payment date and include sales tax.
 */
export function AnalyticsPage({ config }: { config: PortalConfig }) {
  const [range, setRange] = useRange();
  const { data, error } = useApi<Analytics>(config, `/reports/analytics?from=${range.from}&to=${range.to}`);
  const t = data?.tiles, b = data?.before;

  return (
    <div className="report-page">
      <div className="report-head"><h2>Dashboard</h2>
        {data && <span className="muted small">Compared with {prettyDay(data.previous.from)} – {prettyDay(data.previous.to)}</span>}
      </div>
      <RangePicker range={range} onChange={setRange} />
      {error && <p className="error">{error.message}</p>}
      {!data && !error && <p className="muted">Loading…</p>}
      {t && b && data && (
        <>
          <div className="tiles">
            <Tile label="Sales (incl. tax)" value={t.sales} before={b.sales} money hint="Payments less refunds, by payment date" />
            <Tile label="Profit" value={t.profit} before={b.profit} money hint="Tickets paid in full in the range: invoice totals less sales tax and their expenses" />
            <Tile label="Expenses" value={t.expenses} before={b.expenses} money invert hint="Expenses of the tickets paid in full in the range" />
            <Tile label="Paid tickets" value={t.paidTickets} before={b.paidTickets} hint="Tickets that became paid in full in the range" />
            <Tile label="Average invoice" value={t.avgInvoice} before={b.avgInvoice} money hint="Average invoice total (incl. tax) of the tickets paid in full in the range" />
            <Tile label="Refunds" value={t.refunds} before={b.refunds} money invert />
            <Tile label="New leads" value={t.newLeads} before={b.newLeads} />
            <Tile label="Checked in" value={t.checkedIn} before={b.checkedIn} />
            <Tile label="Shipped" value={t.shipped} before={b.shipped} />
            <Tile label="Sales tax" value={t.tax} before={b.tax} money hint="Tax of the tickets paid in full in the range" />
            <Tile label="Insurance" value={t.insurance} before={b.insurance} money hint="Insurance of the tickets paid in full in the range" />
            <Tile label="Transactions" value={t.transactions} before={b.transactions} />
          </div>

          <div className="dash-grid">
            <section className="panel wide"><h3>Sales by month</h3>
              <p className="muted small">The 12 months to {new Date(`${data.monthly.at(-1)?.month ?? range.to.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "long", year: "numeric" })}, with the same months a year earlier.</p>
              <Bars groups={data.monthly.map((m) => ({ label: monthLabel(m.month), values: [m.sales, m.lastYear] }))} series={["Sales", "Year before"]} format={compact} label="Sales by month" />
            </section>

            <section className="panel wide"><h3>Check-ins and shipped by week</h3>
              <Bars groups={data.weekly.map((w) => ({ label: weekLabel(w.week), values: [w.checkedIn, w.shipped] }))} series={["Checked in", "Shipped"]} format={(n) => (Number.isInteger(n) ? String(n) : n.toFixed(1))} label="Check-ins and shipped by week" />
            </section>

            <section className="panel"><h3>Leads made in range</h3>
              <p className="stat-line"><strong>{data.conversion.leads.toLocaleString()}</strong> leads · <strong>{pct(data.conversion.arrived, data.conversion.leads)}</strong> arrived</p>
              <Split label="Lead outcome" parts={[
                { label: "Arrived", value: data.conversion.arrived, tone: "a" },
                { label: "Still out", value: data.conversion.open, tone: "c" },
                { label: "Archived", value: data.conversion.archived, tone: "b" },
              ]} />
            </section>

            <section className="panel"><h3>Estimates sent in range</h3>
              <p className="stat-line"><strong>{data.approval.sent.toLocaleString()}</strong> sent · <strong>{pct(data.approval.accepted, data.approval.accepted + data.approval.declined)}</strong> approved of those answered</p>
              <Split label="Estimate answers" parts={[
                { label: "Approved", value: data.approval.accepted, tone: "a" },
                { label: "Waiting", value: data.approval.waiting, tone: "c" },
                { label: "Declined", value: data.approval.declined, tone: "b" },
              ]} />
            </section>

            <section className="panel"><h3>Turnaround</h3>
              <p className="muted small">Check-in to shipped, for tickets shipped in the range.</p>
              <div className="big-stats">
                <div><strong>{data.turnaround.avgDays ?? "—"}</strong><span>average days</span></div>
                <div><strong>{data.turnaround.medianDays ?? "—"}</strong><span>typical (median) days</span></div>
                <div><strong>{data.turnaround.tickets.toLocaleString()}</strong><span>tickets shipped</span></div>
              </div>
            </section>

            <section className="panel"><h3>Sales by payment method</h3>
              <HBars label="Sales by payment method" format={(n) => usd(n, false)}
                rows={data.methods.map((m) => ({ label: METHOD[m.method] ?? m.method, value: m.amount, note: `${m.count}` }))} />
            </section>

            <section className="panel"><h3>Top services</h3>
              <p className="muted small">On tickets paid in full in the range, by revenue (count in grey).</p>
              <HBars label="Top services" format={(n) => usd(n, false)} rows={data.services.map((s) => ({ label: s.name, value: s.revenue, note: `${s.count}` }))} />
            </section>

            <section className="panel"><h3>Sales by state</h3>
              <HBars label="Sales by state" format={(n) => usd(n, false)} rows={data.states.map((s) => ({ label: s.state || "(none)", value: s.amount }))} />
            </section>

            <section className="panel wide"><h3>Open work right now</h3>
              <p className="muted small">Checked-in tickets that are not finished, by status. <Link to="/tickets">Open the queue</Link></p>
              <HBars label="Open tickets by status" format={(n) => n.toLocaleString()} rows={data.pipeline.map((p) => ({ label: p.status, value: p.count }))} />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
